import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useMsal } from "@azure/msal-react";
import { postAskAi } from "../api";
import { acquireCopilotStudioToken } from "../auth/token";
import { CompanyBrowserModal } from "../company-browser/CompanyBrowserModal";
import { useSpeechToText } from "../hooks/useSpeechToText";
import type { AskAiAttachmentMeta, AskAiMessage } from "../types";

type DraftFile = {
  id: string;
  file: File;
  previewUrl?: string;
};

function fileId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        d="M16.5 7.5 9 15a3 3 0 1 1-4.2-4.2l8.5-8.5a4.5 4.5 0 0 1 6.4 6.4L11 17.4"
      />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M12 3.5a3 3 0 0 1 3 3V11a3 3 0 0 1-6 0V6.5a3 3 0 0 1 3-3z"
      />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6.5 11a5.5 5.5 0 0 0 11 0M12 16.5V20" />
    </svg>
  );
}

function AskCapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="11" cy="11" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="m16 16 4 4" />
    </svg>
  );
}

function CrmCapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M5 6.5h14v12.5H5z"
      />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M8 4.5v3M16 4.5v3M8 11.5h8M8 15h5" />
    </svg>
  );
}

function EnrichCapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 16.5V5.5M8 9l4-4 4 4"
      />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6 16.5v3h12v-3" />
    </svg>
  );
}

const ASK_AI_CAPABILITIES = [
  {
    title: "Ask",
    body: "Accounts, contacts, booths, and the floor plan.",
    Icon: AskCapIcon,
  },
  {
    title: "Update CRM",
    body: "Request notes, tags, follow-ups, and other record changes in plain language.",
    Icon: CrmCapIcon,
  },
  {
    title: "Enrich from files",
    body: "Upload files for accounts or contacts. Ironfleet enriches them and adds them to CRM.",
    Icon: EnrichCapIcon,
  },
] as const;

export function AskAiModal({ onClose }: { onClose: () => void }) {
  const { instance } = useMsal();
  const [messages, setMessages] = useState<AskAiMessage[]>([]);
  const [conversationId, setConversationId] = useState("");
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<DraftFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<DraftFile[]>([]);
  filesRef.current = files;

  const appendTranscript = useCallback((text: string) => {
    setDraft((current) => {
      const prefix = current.trimEnd();
      return prefix ? `${prefix} ${text}` : text;
    });
  }, []);
  const speech = useSpeechToText(appendTranscript);

  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    const area = inputRef.current;
    if (!area) return;
    area.style.height = "auto";
    area.style.height = `${Math.min(area.scrollHeight, 140)}px`;
  }, [draft]);

  useEffect(() => {
    return () => {
      for (const item of filesRef.current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
    };
  }, []);

  function addFiles(list: FileList | null) {
    if (!list?.length) return;
    setFiles((current) => {
      const next = [...current];
      for (const file of Array.from(list)) {
        const id = fileId(file);
        if (next.some((item) => item.id === id)) continue;
        next.push({
          id,
          file,
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
        });
      }
      return next;
    });
    if (fileRef.current) fileRef.current.value = "";
  }

  function removeFile(id: string) {
    setFiles((current) => {
      const target = current.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  }

  async function send() {
    const content = draft.trim() || (files.length ? "(attached files)" : "");
    if (!content || busy) return;
    const attachments: AskAiAttachmentMeta[] = files.map((item) => ({
      name: item.file.name,
      type: item.file.type || "application/octet-stream",
      size: item.file.size,
    }));
    const userMessage: AskAiMessage = { role: "user", content };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setDraft("");
    setFiles((current) => {
      for (const item of current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
      return [];
    });
    setBusy(true);
    setError(null);
    speech.stop();
    try {
      let copilotToken: string | undefined;
      let tokenError: Error | null = null;
      try {
        copilotToken = await acquireCopilotStudioToken(instance);
      } catch (error) {
        tokenError = error instanceof Error ? error : new Error("Unable to acquire Ask AI token");
      }
      const reply = await postAskAi(
        { messages: nextMessages, attachments, conversation_id: conversationId || undefined },
        copilotToken,
      );
      if (!copilotToken && tokenError && reply.content.startsWith("Ask AI is not connected to a model yet.")) {
        throw tokenError;
      }
      if (reply.conversation_id) {
        setConversationId(reply.conversation_id);
      }
      setMessages((current) => [...current, { role: reply.role, content: reply.content }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ask AI failed");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  function onComposerKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  }

  const canSend = Boolean(draft.trim() || files.length) && !busy;

  return (
    <CompanyBrowserModal title="Ask AI" onClose={onClose} className="ask-ai-modal">
      <header className="company-modal-header">
        <div className="company-modal-heading">
          <div className="company-modal-identity">
            <h2>Ask AI</h2>
            <p className="ask-ai-header-lead">Show intelligence and headless CRM.</p>
          </div>
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </header>
      <div className="company-modal-body ask-ai-body">
        <div className="ask-ai-thread" ref={threadRef}>
          {messages.length === 0 && !busy ? (
            <div className="ask-ai-welcome">
              <p className="ask-ai-welcome-lead">
                Ask questions, request CRM updates, or attach files to add enriched accounts and contacts.
              </p>
              <ul className="ask-ai-capabilities">
                {ASK_AI_CAPABILITIES.map(({ title, body, Icon }) => (
                  <li key={title}>
                    <span className="ask-ai-cap-icon">
                      <Icon />
                    </span>
                    <strong>{title}</strong>
                    <p>{body}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            messages.map((item, index) => (
              <div key={`${item.role}-${index}`} className={`ask-ai-bubble ${item.role}`}>
                <span className="ask-ai-bubble-role">{item.role === "user" ? "You" : "Ask AI"}</span>
                <p>{item.content}</p>
              </div>
            ))
          )}
          {busy ? (
            <div className="ask-ai-bubble assistant pending">
              <span className="ask-ai-bubble-role">Ask AI</span>
              <p>Thinking…</p>
            </div>
          ) : null}
        </div>
        <div className="ask-ai-composer">
          {files.length ? (
            <ul className="ask-ai-chips">
              {files.map((item) => (
                <li key={item.id}>
                  {item.previewUrl ? <img src={item.previewUrl} alt="" /> : null}
                  <span>
                    {item.file.name}
                    <em>{formatSize(item.file.size)}</em>
                  </span>
                  <button type="button" className="ghost" onClick={() => removeFile(item.id)} aria-label={`Remove ${item.file.name}`}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKey}
            placeholder="Ask a question, request a CRM update, or attach a file"
            aria-label="Ask a question, request a CRM update, or attach a file"
          />
          <div className="ask-ai-composer-bar">
            <input
              ref={fileRef}
              type="file"
              multiple
              className="visually-hidden"
              onChange={(event) => addFiles(event.target.files)}
            />
            <button
              type="button"
              className="map-control-btn workspace-filter-btn"
              aria-label="Attach files"
              title="Attach files"
              onClick={() => fileRef.current?.click()}
            >
              <PaperclipIcon />
            </button>
            <button
              type="button"
              className={`map-control-btn workspace-filter-btn${speech.listening ? " on" : ""}`}
              aria-label={speech.listening ? "Stop microphone" : "Voice to text"}
              title={speech.available ? (speech.listening ? "Stop microphone" : "Voice to text") : "Speech not available in this browser"}
              aria-pressed={speech.listening}
              disabled={!speech.available}
              onClick={speech.toggle}
            >
              <MicIcon />
            </button>
            <button type="button" className="primary" disabled={!canSend} onClick={() => void send()}>
              Send
            </button>
          </div>
          {!speech.available ? <p className="muted">Speech not available in this browser</p> : null}
          {error ? <p className="err">{error}</p> : null}
        </div>
      </div>
    </CompanyBrowserModal>
  );
}
